// @ts-strict-ignore
import Cookies from 'js-cookie'

import { TCartItem } from '@/entities/cart'

import { _TProduct, TProductCode } from '@/shared/types/deprecated-types'

import { checkMindBoxCLI } from '../lib/checkMindBoxCLI'
import { updateMindbox } from '../lib/updateMindbox'

export const MIND_BOX = `mindbox`

type SendServicePageViewProps = {
	mobilePhone?: string
	callingFrom?: string
	urlPage: string
	sum?: number
	additionalInformation?: string
}

/**
 * @see https://docs.google.com/document/d/1oen-3NrPkcOrAAgjqUJKffrRrnXKDMD9hNR-qk09AHc/edit#
 */
export class MindBoxSDK {
	/**
	 * Viewing the product page event (Online.ProductView.JSNew)
	 * @param productCode - Product KOD value
	 */
	public sendProductPageViewEvent(productCode: TProductCode, userPhone?: string): void {
		if (checkMindBoxCLI()) {
			window[MIND_BOX](`async`, {
				operation: `JS.ViewProduct`,
				data: {
					viewProduct: {
						product: {
							ids: {
								iportid: productCode
							}
						}
					},
					...(userPhone && {
						customer: {
							mobilePhone: userPhone
						}
					})
				},
				onSuccess: async () => {
					updateMindbox()
				}
			})
		}
	}

	/**
	 *  Viewing the category page (Online.ViewCategory.JS)
	 */
	public sendSuccessTradeInCalculate({
		userPhone,
		additionalInformation,
		sum,
		urlPage
	}: {
		userPhone: string
		additionalInformation: string
		sum: number
		urlPage: string
	}): void {
		if (checkMindBoxCLI()) {
			const deviceUUID = Cookies.get('mindboxDeviceUUID')

			window[MIND_BOX](`async`, {
				operation: `JS.SuccessTradeInCalculate`,
				...(deviceUUID && {
					deviceUUID
				}),
				data: {
					customerAction: {
						customFields: {
							additionalInformation,
							sum,
							urlPage
						}
					},
					...(userPhone && {
						customer: {
							mobilePhone: userPhone
						}
					})
				},
				onSuccess: () => {}
			})
		}
	}

	/**
	 *  Viewing the category page (Online.ViewCategory.JS)
	 */
	public sendCategoryPageViewEvent(categoryId: string, userPhone?: string): void {
		if (checkMindBoxCLI()) {
			window[MIND_BOX](`async`, {
				operation: `JS.ViewCategory`,
				data: {
					viewProductCategory: {
						productCategory: {
							ids: {
								iportid: categoryId
							}
						}
					},
					...(userPhone && {
						customer: {
							mobilePhone: userPhone
						}
					})
				},
				onSuccess: () => {}
			})
		}
	}

	/**
	 *  Viewing the category page (Online.ViewCategory.JS)
	 */
	public sendServicePageViewEvent({ mobilePhone, ...rest }: SendServicePageViewProps): void {
		if (checkMindBoxCLI()) {
			window[MIND_BOX](`async`, {
				operation: `JS.ViewServicePage`,
				data: {
					...(mobilePhone && {
						customer: {
							mobilePhone
						}
					}),
					customerAction: {
						customFields: {
							...rest
						}
					}
				},
				onSuccess: () => {}
			})
		}
	}

	public sendLoginInPromoEvent({ mobilePhone, urlPage }: SendServicePageViewProps): void {
		if (checkMindBoxCLI()) {
			window[MIND_BOX](`async`, {
				operation: `Website.LoginInPromo`,
				data: {
					customer: {
						mobilePhone
					},
					customerAction: {
						customFields: {
							urlPage: urlPage
						}
					}
				},
				onSuccess: () => {}
			})
		}
	}

	/**
	 * Cart update event (Online.SetCartItemList.JS.iportNew)
	 */
	public sendCartUpdateEvent = (cartItems: TCartItem[], userPhone?: string): void => {
		if (checkMindBoxCLI() && cartItems) {
			const productList = productListGenerator(cartItems)
			const payload = {
				operation: `JS.SetiportBasket`,
				data: {
					productList,
					...(userPhone && {
						customer: {
							mobilePhone: userPhone
						}
					})
				},
				onSuccess: () => {}
			}
			window[MIND_BOX](`async`, payload)
		}
	}

	/**
	 * Adding a product to the compare list (Online.AddCompareItemList.JS.iportNew)
	 * @param productItem
	 */
	public sendAddToCompareEvent(productItem: _TProduct): void {
		if (checkMindBoxCLI()) {
			window[MIND_BOX](`async`, {
				operation: `Online.AddCompareItemList.JS.iportNew`,
				data: {
					addProductToList: {
						product: {
							ids: {
								iportid: productItem.KOD
							}
						},
						pricePerItem: productItem.PRICE.VALUE
					}
				}
			})
		}
	}

	/**
	 * Removing product from the compare list
	 */
	public sendRemoveFromCompareEvent(productItem: _TProduct): void {
		if (checkMindBoxCLI()) {
			window[MIND_BOX](`async`, {
				operation: `Online.DeleteCompareItemList.JS.iportNew`,
				data: {
					removeProductFromList: {
						product: {
							ids: {
								iportid: productItem.KOD
							}
						},
						pricePerItem: productItem.PRICE.VALUE
					}
				}
			})
		}
	}

	/**
	 * Adding product to the favourites list
	 * @param products - products from favourites list
	 */
	public sendEditFavoritesListEvent(products: _TProduct[], userPhone?: string): void {
		if (checkMindBoxCLI()) {
			window[MIND_BOX](`async`, {
				operation: `JS.SetiportFavorite`,
				data: {
					productList: products.map(({ KOD, PRICE }) => ({
						product: {
							ids: {
								iportid: KOD
							}
						},
						count: 1,
						pricePerItem: PRICE.VALUE
					})),
					...(userPhone && {
						customer: {
							mobilePhone: userPhone
						}
					})
				},
				onSuccess: () => null
			})
		}
	}

	public sendRegistrationOpeningNewStore({ mobilePhone, email, name, pointOfContact }): void {
		if (checkMindBoxCLI()) {
			window[MIND_BOX](`async`, {
				operation: `Online.RegistrationOpeningNewStore`,
				data: {
					customer: {
						mobilePhone,
						email,
						firstName: name,
						subscriptions: [
							{
								pointOfContact: `Email`,
								topic: `iport`
							}
						]
					},
					pointOfContact
				}
			})
		}
	}

	public sendYMClientId(mobilePhone, clientID) {
		if (checkMindBoxCLI()) {
			window[MIND_BOX](`async`, {
				operation: 'ZapisatIdKlientaVYaMetrikaByPhone',
				data: {
					customer: {
						mobilePhone,
						customFields: {
							yaMetrikaID: clientID
						}
					}
				}
			})
		}
	}
}

const productListGenerator = (
	cartItems: TCartItem[]
): {
	product: { ids: Record<string, TProductCode> }
	count: number
	pricePerItem: number
}[] =>
	cartItems.reduce<
		{
			product: { ids: Record<string, TProductCode> }
			count: number
			pricePerItem: number
		}[]
	>((result, { QUANTITY, PRICE, KOD }) => {
		const duplicate = result.find(({ product }) => product.ids.iportid === KOD)

		if (duplicate) {
			duplicate.count += QUANTITY

			return result
		}

		return [
			...result,
			{
				product: {
					ids: {
						iportid: KOD
					}
				},
				count: QUANTITY,
				pricePerItem: PRICE.VALUE
			}
		]
	}, [])
